import React, { useState } from 'react';
import { Check, ChevronDown, ChevronRight, Copy, Database, Download, Loader2, Play, RefreshCw, Shield, SlidersHorizontal, Sparkles, Users } from 'lucide-react';
import { FunctionalSelect, SelectItem } from '../ui/Select';
import Button from '../ui/Button';
import { CriteriaSentence, describeCondition } from './criteriaText';
import { friendlyTableName, getNode } from './criteriaUtils';

export const SCHEDULE_OPTIONS = [
  { value: '', label: 'Not scheduled' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

export const SCHEDULE_DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((d) => ({
  value: d,
  label: d.charAt(0).toUpperCase() + d.slice(1),
}));

export const SCHEDULE_DAYS_OF_MONTH = [
  { value: 'beginning', label: 'Beginning of month' },
  { value: 'end', label: 'End of month' },
  ...Array.from({ length: 28 }, (_, i) => ({ value: String(i + 1), label: `Day ${i + 1}` })),
];

function statusBadgeClass(s) {
  const v = (s || '').toLowerCase();
  if (v === 'complete' || v === 'completed') return 'bg-blue-100 text-blue-800';
  if (v === 'failed') return 'bg-red-100 text-red-800';
  if (v === 'pending') return 'bg-yellow-100 text-yellow-800';
  if (v === 'started' || v === 'processing') return 'bg-green-100 text-green-800';
  return 'bg-gray-100 text-gray-800';
}

const Section = ({ title, icon: Icon, children, right = null }) => (
  <section className="rounded-xl border border-hairline bg-surface p-4 shadow-card">
    <header className="mb-3 flex items-center justify-between gap-2">
      <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
        {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden /> : null}
        {title}
      </h3>
      {right}
    </header>
    {children}
  </section>
);

const CountCard = ({
  count,
  status,
  tookMs,
  onRefresh,
  autoCount,
  onToggleAuto,
  disabled,
  error,
  samplingActive,
  matchingTotal,
  targetSize,
  onTargetSizeChange,
  onFitTargetSize,
  canFitTargetSize,
  shapeIssues = [],
  onHoistSample,
  onLoosenLevel,
  contradictions = [],
  onCombineContradictions,
}) => {
  const isRunning = status === 'running';
  const stale = status === 'stale';
  const subtitle = isRunning
    ? 'Counting distinct profiles on Databricks…'
    : error
      ? error
      : stale
        ? 'Criteria changed — count is out of date.'
        : count == null
          ? 'Finish your criteria to get a count.'
          : samplingActive && matchingTotal != null
            ? `Sampled from ${Number(matchingTotal).toLocaleString()} matching profiles${tookMs ? ` · ${(tookMs / 1000).toFixed(1)}s` : ''}`
            : `Distinct profiles${tookMs ? ` · ${(tookMs / 1000).toFixed(1)}s` : ''}`;
  return (
    <Section
      title="Estimated audience"
      icon={Users}
      right={
        <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-zinc-500">
          <input type="checkbox" checked={autoCount} onChange={(e) => onToggleAuto(e.target.checked)} className="h-3.5 w-3.5 accent-[#0062FB]" />
          Auto-count
        </label>
      }
    >
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className={`font-display text-4xl font-bold tabular-nums tracking-tight ${stale ? 'text-zinc-400' : 'text-[#18181B]'}`} aria-live="polite">
            {isRunning && count == null ? <span className="text-zinc-300">…</span> : count == null ? <span className="text-zinc-300">—</span> : Number(count).toLocaleString()}
          </p>
          <p className="mt-1 text-xs text-zinc-500">{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={disabled || isRunning}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-hairline bg-surface px-3 text-sm font-medium text-[#18181B] hover:bg-[#E8EEF5] disabled:cursor-not-allowed disabled:opacity-50"
          title="Run count now"
        >
          {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Count
        </button>
      </div>

      {contradictions.length ? (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" role="status">
          <p>
            {contradictions.length === 1
              ? `An "All of" level asks ${contradictions[0].label} to be more than one value at once, so nothing can match it.`
              : `${contradictions.length} "All of" levels ask one attribute to be more than one value at once, so nothing can match them.`}{' '}
            A profile has a single {contradictions.length === 1 ? contradictions[0].label.toLowerCase() : 'value per attribute'}, so these
            conditions have to be combined or one of them removed.
          </p>
          {contradictions.some((c) => c.canMerge) ? (
            <button
              type="button"
              onClick={onCombineContradictions}
              className="mt-2 inline-flex h-7 items-center rounded-md border border-red-300 bg-white px-2.5 font-medium text-red-900 hover:bg-red-100"
              title='Restate them as one "is any of" condition, so a profile matching any of those values qualifies'
            >
              Combine into "is any of"
            </button>
          ) : null}
        </div>
      ) : null}

      {shapeIssues.length ? (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="status">
          <p>
            {shapeIssues.length === 1
              ? `A group is taking ${shapeIssues[0].pct}% inside an "All of" level, so it narrows the other conditions instead of adding to them.`
              : `${shapeIssues.length} groups take a percentage inside an "All of" level, so they narrow the other conditions instead of adding to them.`}{' '}
            Percentages only add up across "Any of" levels, so the estimates and Fit are unreliable until this is settled.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onHoistSample}
              className="inline-flex h-7 items-center rounded-md border border-amber-300 bg-white px-2.5 font-medium text-amber-900 hover:bg-amber-100"
              title="Move the percentage up to the level that combines these conditions. This keeps exactly the same profiles."
            >
              Take the percentage of the whole level
            </button>
            <button
              type="button"
              onClick={onLoosenLevel}
              className="inline-flex h-7 items-center rounded-md border border-amber-300 bg-white px-2.5 font-medium text-amber-900 hover:bg-amber-100"
              title="Switch that level to Any of so each group contributes its own slice. This widens the audience."
            >
              Switch that level to "Any of"
            </button>
          </div>
          <p className="mt-1.5 text-[11px] text-amber-700">
            The first keeps the same people. The second widens the audience, which is what you want for
            "some of this group plus all of that one".
          </p>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-hairline pt-3">
        <label htmlFor="segment-target-size" className="text-xs text-zinc-500">
          Target size
        </label>
        <input
          id="segment-target-size"
          type="text"
          inputMode="numeric"
          value={targetSize || ''}
          onChange={(e) => onTargetSizeChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canFitTargetSize) {
              e.preventDefault();
              onFitTargetSize();
            }
          }}
          placeholder="e.g. 500000"
          className="h-8 w-28 rounded border border-input bg-surface px-2 text-sm tabular-nums text-[#18181B] placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#18181B]/10"
        />
        <button
          type="button"
          onClick={onFitTargetSize}
          disabled={!canFitTargetSize || !String(targetSize || '').trim()}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-hairline bg-surface px-2.5 text-xs font-medium text-[#18181B] hover:bg-[#F3F2EE] disabled:cursor-not-allowed disabled:opacity-50"
          title={canFitTargetSize ? 'Adjust the group percentages to land on this size' : 'Run a count first so group sizes are known'}
        >
          <SlidersHorizontal className="h-3.5 w-3.5 text-zinc-500" />
          Fit
        </button>
      </div>
    </Section>
  );
};

const ConditionImpactCard = ({ criteria, conditionCount, status, results, error, onAnalyze }) => {
  const isRunning = status === 'running';
  const canAnalyze = conditionCount > 0;
  const rows =
    status === 'done' && results
      ? results.conditions
          .map((r) => {
            const node = getNode(criteria, r.path);
            const label = node ? describeCondition(node) : null;
            return { ...r, label };
          })
          .sort((a, b) => (b.delta ?? -Infinity) - (a.delta ?? -Infinity))
      : [];

  return (
    <Section
      title="Condition impact"
      icon={Sparkles}
      right={
        <button
          type="button"
          onClick={onAnalyze}
          disabled={!canAnalyze || isRunning}
          className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-lg border border-hairline bg-surface px-2.5 text-xs font-medium text-[#18181B] hover:bg-[#E8EEF5] disabled:cursor-not-allowed disabled:opacity-50"
          title="Run count with each condition removed, one at a time"
        >
          {isRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          Analyze
        </button>
      }
    >
      {isRunning ? (
        <p className="text-sm text-zinc-500">
          Running {conditionCount} count{conditionCount === 1 ? '' : 's'} with one condition removed at a time…
        </p>
      ) : status === 'error' ? (
        <p className="text-sm text-red-600 whitespace-pre-line">{error}</p>
      ) : status === 'done' ? (
        rows.length ? (
          <ul className="space-y-2">
            {rows.map((r, i) => (
              <li key={i} className="flex items-start justify-between gap-3 text-sm">
                <span className="min-w-0 truncate text-zinc-700">
                  {r.label ? (
                    <>
                      <span className="font-medium text-[#18181B]">{r.label.column}</span> {r.label.operator} {r.label.value}
                    </>
                  ) : (
                    'Condition'
                  )}
                </span>
                {r.error ? (
                  <span className="shrink-0 text-xs text-red-600">Failed</span>
                ) : (
                  <span className={`shrink-0 font-medium tabular-nums ${r.delta > 0 ? 'text-emerald-600' : r.delta < 0 ? 'text-zinc-400' : 'text-zinc-500'}`}>
                    {r.delta > 0 ? '+' : ''}
                    {Number(r.delta).toLocaleString()}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-zinc-400">No conditions to analyze.</p>
        )
      ) : (
        <p className="text-sm text-zinc-400">
          {canAnalyze ? 'See how much the audience would grow if you removed each condition.' : 'Add a condition to analyze.'}
        </p>
      )}
    </Section>
  );
};

const HowItRuns = ({ explain, organizationName, showTechnicalDetails }) => {
  if (!explain) return <p className="text-sm text-zinc-400">Add a complete condition to see how this query runs.</p>;
  const tables = explain.tables || [];
  const sampled = explain.sampled_groups || [];
  const joinText =
    explain.join_strategy === 'planned_joins'
      ? 'joined on profile MD5'
      : explain.join_strategy === 'cross_join'
        ? 'no join metadata — results may be inflated'
        : '';
  return (
    <ul className="space-y-1.5 text-sm text-zinc-700">
      {showTechnicalDetails ? (
        <li className="flex items-start gap-2">
          <Database className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
          <span>
            {tables.length ? tables.map((t) => friendlyTableName(t.table) || t.table).join(', ') : 'No tables'}
            {tables.length > 1 && joinText ? <span className={explain.join_strategy === 'cross_join' ? ' text-amber-700' : ' text-zinc-500'}> · {joinText}</span> : null}
          </span>
        </li>
      ) : null}
      <li className="flex items-start gap-2">
        <Shield className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
        <span>
          {explain.universe_scoped ? `Limited to ${organizationName || 'this organization'}'s universe` : 'Not limited to an organization universe'}
          {explain.publisher_filtered
            ? showTechnicalDetails && (explain.publisher_ids || []).length
              ? ` · Everflow rows limited to publisher${explain.publisher_ids.length === 1 ? '' : 's'} ${explain.publisher_ids.join(', ')}`
              : " · Everflow rows limited to your organization's publishers"
            : ''}
        </span>
      </li>
      {sampled.length ? (
        <li className="flex items-start gap-2">
          <SlidersHorizontal className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
          <span>
            {sampled.length === 1
              ? `One group is limited to ${sampled[0].sample_pct}% of its matches`
              : `${sampled.length} groups are limited to a percentage of their matches`}
            <span className="text-zinc-500"> · the same profiles are picked on every run until you reshuffle</span>
          </span>
        </li>
      ) : null}
    </ul>
  );
};

const SqlBlock = ({ sql, error }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sql || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      /* ignore */
    }
  };
  return (
    <div>
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-sm font-medium text-[#18181B]" aria-expanded={open}>
          {open ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
          Databricks SQL
        </button>
        {sql ? (
          <button type="button" onClick={copy} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-[#18181B]" title="Copy SQL">
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        ) : null}
      </div>
      {error ? <p className="mt-2 text-xs text-red-600 whitespace-pre-line">{error}</p> : null}
      {open ? (
        <pre className="mt-2 max-h-72 overflow-auto rounded-lg bg-[#18181B] p-3 font-mono text-[11px] leading-relaxed text-zinc-100">
          {sql || '-- SQL will appear once criteria are complete'}
        </pre>
      ) : null}
    </div>
  );
};

/**
 * Right-hand pane of the segment editor: live count, English summary, run details,
 * SQL, schedule, and export controls.
 */
const SegmentSummaryPane = ({
  criteria,
  sql,
  sqlError,
  explain,
  organizationName,
  showTechnicalDetails = false,
  count,
  countStatus,
  countTookMs,
  countError,
  onRunCount,
  autoCount,
  onToggleAutoCount,
  canRunCount,
  analyzeStatus,
  analyzeResults,
  analyzeError,
  onAnalyze,
  conditionCount = 0,
  samplingActive = false,
  matchingTotal = null,
  targetSize = '',
  onTargetSizeChange,
  onFitTargetSize,
  canFitTargetSize = false,
  shapeIssues = [],
  onHoistSample,
  onLoosenLevel,
  contradictions = [],
  onCombineContradictions,
  schedule,
  onScheduleChange,
  scheduleDayOfWeek,
  onScheduleDayOfWeekChange,
  scheduleDayOfMonth,
  onScheduleDayOfMonthChange,
  segmentId,
  executionId,
  runStatus,
  exporting,
  onExport,
  onDownload,
  exportDisabledReason,
}) => {
  const runLower = (runStatus || '').toLowerCase();
  const canDownload = Boolean(executionId) && (runLower === 'complete' || runLower === 'completed');
  const inFlight = runLower === 'pending' || runLower === 'started' || runLower === 'processing';

  return (
    <div className="space-y-4">
      <CountCard
        count={count}
        status={countStatus}
        tookMs={countTookMs}
        error={countError}
        onRefresh={onRunCount}
        autoCount={autoCount}
        onToggleAuto={onToggleAutoCount}
        disabled={!canRunCount}
        samplingActive={samplingActive}
        matchingTotal={matchingTotal}
        targetSize={targetSize}
        onTargetSizeChange={onTargetSizeChange}
        onFitTargetSize={onFitTargetSize}
        canFitTargetSize={canFitTargetSize}
        shapeIssues={shapeIssues}
        onHoistSample={onHoistSample}
        onLoosenLevel={onLoosenLevel}
        contradictions={contradictions}
        onCombineContradictions={onCombineContradictions}
      />

      <ConditionImpactCard
        criteria={criteria}
        conditionCount={canRunCount ? conditionCount : 0}
        status={analyzeStatus}
        results={analyzeResults}
        error={analyzeError}
        onAnalyze={onAnalyze}
      />

      <Section title="In plain English">
        <p className="text-sm leading-relaxed">
          <CriteriaSentence criteria={criteria} />
        </p>
      </Section>

      <Section title="How it runs">
        <HowItRuns explain={explain} organizationName={organizationName} showTechnicalDetails={showTechnicalDetails} />
        {showTechnicalDetails ? (
          <div className="mt-3 border-t border-hairline pt-3">
            <SqlBlock sql={sql} error={sqlError} />
          </div>
        ) : sqlError ? (
          <p className="mt-3 border-t border-hairline pt-3 text-xs text-red-600 whitespace-pre-line">{sqlError}</p>
        ) : null}
      </Section>

      <Section title="Refresh schedule">
        <div className="space-y-2">
          <FunctionalSelect value={schedule || ''} onValueChange={(v) => onScheduleChange(v || '')} placeholder="Not scheduled">
            {SCHEDULE_OPTIONS.map((o) => (
              <SelectItem key={o.value || 'none'} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </FunctionalSelect>
          {schedule === 'weekly' ? (
            <FunctionalSelect value={scheduleDayOfWeek || ''} onValueChange={(v) => onScheduleDayOfWeekChange(v || '')} placeholder="Choose a day">
              {SCHEDULE_DAYS_OF_WEEK.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </FunctionalSelect>
          ) : null}
          {schedule === 'monthly' ? (
            <FunctionalSelect value={scheduleDayOfMonth || ''} onValueChange={(v) => onScheduleDayOfMonthChange(v || '')} placeholder="Choose a day">
              {SCHEDULE_DAYS_OF_MONTH.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </FunctionalSelect>
          ) : null}
          <p className="text-xs text-zinc-500">
            {schedule ? 'A fresh file is produced on this schedule and the count is updated.' : 'Scheduled segments re-run automatically and keep a fresh download ready.'}
          </p>
        </div>
      </Section>

      <Section
        title="Export"
        icon={Download}
        right={
          runStatus ? <span className={`rounded px-2 py-0.5 text-xs font-medium ${statusBadgeClass(runStatus)}`}>{runStatus}</span> : null
        }
      >
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onExport}
              disabled={Boolean(exportDisabledReason) || exporting || inFlight}
              className="gap-1.5"
              title={exportDisabledReason || 'Build a downloadable file of matching MD5s'}
            >
              {exporting || inFlight ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              {inFlight ? 'Building file…' : 'Create file'}
            </Button>
            {canDownload ? (
              <button
                type="button"
                onClick={onDownload}
                className="inline-flex h-10 items-center gap-1.5 rounded-md border border-hairline bg-surface px-4 text-sm font-medium text-[#18181B] hover:bg-[#E8EEF5]"
              >
                <Download className="h-4 w-4" />
                Download
              </button>
            ) : null}
          </div>
          {exportDisabledReason ? <p className="text-xs text-zinc-500">{exportDisabledReason}</p> : null}
          {executionId && showTechnicalDetails ? (
            <p className="text-[11px] text-zinc-400">
              Execution <code className="rounded bg-[#E8EEF5] px-1 font-mono">{executionId}</code>
            </p>
          ) : segmentId && !executionId ? (
            <p className="text-xs text-zinc-500">No file yet. Create one to download the matching MD5s.</p>
          ) : null}
        </div>
      </Section>
    </div>
  );
};

export default SegmentSummaryPane;
